import type { PixelCrop } from 'react-image-crop';
import { logger } from '@/lib/telemetry/logger';

/**
 * Render a `PixelCrop` region of `image` into a square (or arbitrary size)
 * PNG `Blob` at the requested output size. Pure DOM/canvas work — no React,
 * no SDK calls. Lifted out of `confirm-image-modal.tsx` so that any surface
 * with an inline `<ReactCrop>` can produce the cropped bytes the same way.
 *
 * ReactCrop's onComplete returns PixelCrop in the *rendered* display
 * coordinate system, so we must convert to natural-image coordinates using
 * the SAME rendered dimensions ReactCrop measures against. `image.width`
 * reads the HTML width attribute (not always CSS dims) — use clientWidth /
 * clientHeight for the unambiguous rendered box.
 */
export function getCroppedBlob(
  image: HTMLImageElement,
  pixelCrop: PixelCrop,
  outputSize: { w: number; h: number },
): Promise<Blob> {
  const canvas = drawCropToCanvas(image, pixelCrop, outputSize);
  if (canvas === null) {
    return Promise.reject(new Error('Canvas context unavailable'));
  }
  return canvasToPngBlob(canvas);
}

/**
 * Same crop draw as `getCroppedBlob`, but also produces a bounded WebP
 * data-URI of the cropped region for durable localStorage draft persistence
 * (MOR-847: market drafts must survive a reload before the deferred upload).
 *
 * The PNG `File` is the in-session submit payload (unchanged bytes). The
 * `persistedBytes` data-URI is a re-encoded, size-capped copy the store writes
 * to localStorage and reconstructs a `File` from on rehydrate. It is
 * best-effort: `null` when the environment can't encode WebP or the image
 * exceeds the per-image ceiling even after a lower-quality retry; the pick
 * still succeeds, the draft simply won't survive a reload. A reloaded draft
 * therefore uploads the (visually identical, ≤1024²) WebP rather than the PNG.
 *
 * `outputSize` is a ceiling: a crop region smaller than it is exported at its
 * own pixel size, because upscaling adds bytes but no detail (MOR-2292).
 */
export async function getCroppedDraftImage(
  image: HTMLImageElement,
  pixelCrop: PixelCrop,
  outputSize: { w: number; h: number },
  fileNameStem: string,
): Promise<{ file: File; persistedBytes: string | null }> {
  const canvas = drawCropToCanvas(
    image,
    pixelCrop,
    capToSourcePixels(image, pixelCrop, outputSize, fileNameStem),
  );
  if (canvas === null) {
    throw new Error('Canvas context unavailable');
  }
  const blob = await canvasToPngBlob(canvas);
  const file = new File([blob], `${fileNameStem}.png`, { type: 'image/png' });
  return { file, persistedBytes: encodeBoundedWebp(canvas) };
}

function capToSourcePixels(
  image: HTMLImageElement,
  pixelCrop: PixelCrop,
  outputSize: { w: number; h: number },
  fileNameStem: string,
): { w: number; h: number } {
  const displayedW = image.clientWidth || image.width;
  const sourceW = Math.floor((pixelCrop.width * image.naturalWidth) / displayedW);
  if (!(sourceW > 0) || sourceW >= outputSize.w) return outputSize;
  logger.warn('proposal-form.image_source_below_output', {
    fileNameStem,
    sourceW,
    outputW: outputSize.w,
  });
  return { w: sourceW, h: Math.max(1, Math.round((outputSize.h * sourceW) / outputSize.w)) };
}

/** Per-image cap on the persisted data-URI string length (~chars ≈ storage
 *  cost). A 1024² WebP@0.82 is well under this; the retry + null fallback keep
 *  a pathological image from bloating the localStorage draft. */
const MAX_PERSISTED_DATAURI_CHARS = 700_000;
const WEBP_QUALITY = 0.82;
const WEBP_QUALITY_RETRY = 0.6;

/** Re-encode the drawn canvas to a bounded WebP data-URI, or null if it can't
 *  be produced within the ceiling. Never throws; persistence is best-effort. */
function encodeBoundedWebp(canvas: HTMLCanvasElement): string | null {
  try {
    let dataUri = canvas.toDataURL('image/webp', WEBP_QUALITY);
    if (!dataUri.startsWith('data:image/webp')) {
      // Browser lacks canvas-WebP encode (older Safari) → toDataURL fell back
      // to PNG. Keep it only if it fits; otherwise a lossy retry won't help.
      return dataUri.length <= MAX_PERSISTED_DATAURI_CHARS ? dataUri : logTooLarge(dataUri.length);
    }
    if (dataUri.length > MAX_PERSISTED_DATAURI_CHARS) {
      dataUri = canvas.toDataURL('image/webp', WEBP_QUALITY_RETRY);
    }
    if (dataUri.length > MAX_PERSISTED_DATAURI_CHARS) {
      return logTooLarge(dataUri.length);
    }
    return dataUri;
  } catch (err) {
    // Tainted canvas or unsupported encoder: persistence is best-effort and the
    // in-session pick is unaffected. Rare in practice (the picker proxies images
    // same-origin to keep the canvas clean), so a warn here is a real signal.
    logger.warn('proposal-form.draft_image_encode_failed', {
      message: err instanceof Error ? err.message.slice(0, 200) : 'unknown',
    });
    return null;
  }
}

function logTooLarge(chars: number): null {
  logger.warn('proposal-form.draft_image_too_large', {
    chars,
    ceiling: MAX_PERSISTED_DATAURI_CHARS,
  });
  return null;
}

/** Draw the crop region into a fresh canvas at `outputSize`. Returns null when
 *  a 2D context is unavailable. Shared by both public producers above. */
function drawCropToCanvas(
  image: HTMLImageElement,
  pixelCrop: PixelCrop,
  outputSize: { w: number; h: number },
): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas');
  canvas.width = outputSize.w;
  canvas.height = outputSize.h;
  const ctx = canvas.getContext('2d');
  if (ctx === null) {
    return null;
  }

  const displayedW = image.clientWidth || image.width;
  const displayedH = image.clientHeight || image.height;
  const scaleX = image.naturalWidth / displayedW;
  const scaleY = image.naturalHeight / displayedH;

  // Source rectangle in natural-image pixels.
  let sx = pixelCrop.x * scaleX;
  let sy = pixelCrop.y * scaleY;
  let sw = pixelCrop.width * scaleX;
  let sh = pixelCrop.height * scaleY;

  // Clamp the source rectangle inside the image so the destination canvas is
  // always fully painted. If the crop rect spills past the image edge (a
  // measurement/scale mismatch between ReactCrop's display basis and
  // `clientWidth`, or a crop dragged to the boundary), `drawImage` samples
  // transparent pixels there — which then show the container's grey background
  // through the circular avatar mask (MOR-803, "grey corner in the preview").
  // Shrink toward the rect's centre to preserve aspect when it's larger than
  // the image, then shift it fully in-bounds.
  const natW = image.naturalWidth;
  const natH = image.naturalHeight;
  if (natW > 0 && natH > 0 && sw > 0 && sh > 0) {
    if (sw > natW || sh > natH) {
      const cx = sx + sw / 2;
      const cy = sy + sh / 2;
      const shrink = Math.min(natW / sw, natH / sh);
      sw *= shrink;
      sh *= shrink;
      sx = cx - sw / 2;
      sy = cy - sh / 2;
    }
    sx = Math.max(0, Math.min(sx, natW - sw));
    sy = Math.max(0, Math.min(sy, natH - sh));
  }

  ctx.drawImage(image, sx, sy, sw, sh, 0, 0, outputSize.w, outputSize.h);
  return canvas;
}

function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob === null) {
          reject(new Error('Failed to create image blob'));
          return;
        }
        resolve(blob);
      },
      'image/png',
      1,
    );
  });
}
